import type { EventData } from "@/lib/events/types"

function firstAddressSegment(full: string): string {
  const head = full.split(",")[0]?.trim() ?? ""
  return head
}

/**
 * Primary location line for cards and headers (short human-readable line).
 * Mapbox autocomplete stores a street name in `locationName` (`feature.text`) while the
 * house number lives in `address` (`place_name`); prefer the structured headline when
 * it clearly enriches the label (leading street number, or a longer superset of the name).
 */
export function eventLocationPrimary(
  e: Pick<EventData, "location" | "locationName" | "address">,
): string {
  const nameT = e.locationName?.trim() ?? ""
  const loc = e.location?.trim() ?? ""
  const addr = e.address?.trim() ?? ""

  if (addr) {
    const head = firstAddressSegment(addr)
    if (head) {
      const headHasLeadingNumber = /^\d/.test(head)
      const nameHasLeadingNumber = /^\d/.test(nameT)
      if (headHasLeadingNumber && !nameHasLeadingNumber) {
        return head
      }
      if (nameT) {
        const h = head.toLowerCase()
        const n = nameT.toLowerCase()
        if (h.includes(n) && head.length > nameT.length) {
          return head
        }
      }
    }
  }

  return nameT || loc
}

/** Secondary line (full address) when available. */
export function eventLocationAddress(
  e: Pick<EventData, "address">,
): string | undefined {
  const a = e.address?.trim()
  return a || undefined
}

/** Full location string for detail expand + clipboard (`address` preferred, else `location`). */
export function eventLocationDetailFullText(
  e: Pick<EventData, "address" | "location">,
): string {
  const a = e.address?.trim() ?? ""
  const l = e.location?.trim() ?? ""
  return (a || l).trim()
}

function isLikelyCountrySuffix(part: string): boolean {
  const p = part.toLowerCase()
  return (
    p === "united states" ||
    p === "usa" ||
    p === "united states of america" ||
    p === "uk" ||
    p === "u.k." ||
    p === "united kingdom" ||
    p === "canada" ||
    p === "australia"
  )
}

function stripTrailingUsZip(s: string): string {
  return s.replace(/\s+\d{5}(-\d{4})?\s*$/i, "").trim()
}

/** Lowercase US state / territory full name → USPS abbreviation (for compact city/state line). */
const US_STATE_FULL_TO_ABBR: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  "district of columbia": "DC",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
}

function abbreviateUsRegionForShortLine(region: string): string {
  const stripped = stripTrailingUsZip(region)
  const key = stripped.toLowerCase()
  if (US_STATE_FULL_TO_ABBR[key]) return US_STATE_FULL_TO_ABBR[key]
  if (/^[a-z]{2}$/i.test(stripped)) return stripped.toUpperCase()
  return stripped
}

function abbreviateUsStreetLine(street: string): string {
  let s = street.trim()
  const pairs: [RegExp, string][] = [
    [/\bNortheast\b/gi, "NE"],
    [/\bNorthwest\b/gi, "NW"],
    [/\bSoutheast\b/gi, "SE"],
    [/\bSouthwest\b/gi, "SW"],
    [/\bAvenue\b/gi, "Ave"],
    [/\bBoulevard\b/gi, "Blvd"],
    [/\bStreet\b/gi, "St"],
    [/\bRoad\b/gi, "Rd"],
    [/\bDrive\b/gi, "Dr"],
    [/\bLane\b/gi, "Ln"],
    [/\bCourt\b/gi, "Ct"],
    [/\bPlace\b/gi, "Pl"],
    [/\bParkway\b/gi, "Pkwy"],
    [/\bTerrace\b/gi, "Ter"],
    [/\bApartment\b/gi, "Apt"],
    [/\bSuite\b/gi, "Ste"],
    [/\bNorth\b/gi, "N"],
    [/\bSouth\b/gi, "S"],
    [/\bEast\b/gi, "E"],
    [/\bWest\b/gi, "W"],
  ]
  for (const [re, rep] of pairs) {
    s = s.replace(re, rep)
  }
  return s.replace(/\s+/g, " ").trim()
}

/**
 * Two-line compact address for event detail Location card (no country/ZIP on line 2).
 * Falls back to {@link eventLocationPrimary} when `address` is empty.
 */
export function eventLocationDetailShortLines(
  e: Pick<EventData, "location" | "locationName" | "address">,
): { line1: string; line2: string } {
  const full = e.address?.trim()
  if (!full) {
    const p = eventLocationPrimary(e).trim()
    return { line1: p, line2: "" }
  }

  let parts = full.split(",").map((s) => s.trim()).filter(Boolean)
  if (parts.length === 0) {
    return { line1: eventLocationPrimary(e).trim(), line2: "" }
  }

  while (
    parts.length > 0 &&
    isLikelyCountrySuffix(parts[parts.length - 1]!)
  ) {
    parts = parts.slice(0, -1)
  }

  if (parts.length === 0) {
    return { line1: eventLocationPrimary(e).trim(), line2: "" }
  }

  if (parts.length === 1) {
    return { line1: abbreviateUsStreetLine(parts[0]!), line2: "" }
  }

  if (parts.length === 2) {
    return {
      line1: abbreviateUsStreetLine(parts[0]!),
      line2: abbreviateUsRegionForShortLine(parts[1]!),
    }
  }

  const statePart = parts[parts.length - 1]!
  const cityPart = parts[parts.length - 2]!
  const streetParts = parts.slice(0, -2)
  const line1 = abbreviateUsStreetLine(streetParts.join(", "))
  const line2 = [cityPart, abbreviateUsRegionForShortLine(statePart)].join(", ")
  return { line1, line2 }
}

/**
 * City + region (ZIP stripped, no street) parsed from a comma-separated postal-style string.
 * Used for event cover location and other surfaces that must stay above street level.
 */
function coverCityRegionFromCommaAddressString(full: string): string {
  let parts = full.split(",").map((s) => s.trim()).filter(Boolean)
  while (
    parts.length > 0 &&
    isLikelyCountrySuffix(parts[parts.length - 1]!)
  ) {
    parts = parts.slice(0, -1)
  }
  if (parts.length === 0) return ""

  if (parts.length === 1) {
    const only = parts[0]!
    if (/^\d/.test(only)) return ""
    return stripTrailingUsZip(only).trim()
  }

  if (parts.length === 2) {
    const a = parts[0]!
    const b = parts[1]!
    if (/^\d/.test(a)) {
      return abbreviateUsRegionForShortLine(b).trim()
    }
    return `${a}, ${abbreviateUsRegionForShortLine(b)}`.trim()
  }

  const statePart = parts[parts.length - 1]!
  const cityPart = parts[parts.length - 2]!
  return `${cityPart}, ${abbreviateUsRegionForShortLine(statePart)}`.trim()
}

/**
 * City-level label for the public event cover: parsed from `address`, else from freeform `location`.
 * Never includes the first (street) segment of a structured address.
 */
export function eventLocationCoverCityRegionLine(
  e: Pick<EventData, "location" | "address">,
): string {
  const addr = e.address?.trim()
  if (addr) {
    const fromAddr = coverCityRegionFromCommaAddressString(addr)
    if (fromAddr) return fromAddr
  }
  const loc = e.location?.trim()
  if (loc) return coverCityRegionFromCommaAddressString(loc)
  return ""
}

/** True when full text has more detail than the short two-line summary (show expand). */
export function locationDetailWantsFullExpand(
  fullText: string,
  short: { line1: string; line2: string },
): boolean {
  const norm = (s: string) =>
    s
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
  const shortJoined = norm(
    `${short.line1}${short.line2 ? `, ${short.line2}` : ""}`,
  )
  const fullNorm = norm(fullText)
  if (!fullNorm) return false
  if (!shortJoined) return fullNorm.length > 0
  return fullNorm !== shortJoined
}
